import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Drawer,
  Group,
  NumberInput,
  rem,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { showNotification } from "@dehub/theme";
import { TrashIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReturnApi } from "../../apis";
import type {
  InventoryImpactType,
  IReturnDamagedInventory,
  IReturnItem,
} from "../../apis/return";

type TFn = (k: string, o?: Record<string, unknown>) => string;

const BUCKET_CODES: InventoryImpactType[] = [
  "RESTOCK_GOOD",
  "DAMAGED_SEPARATE_STOCK",
  "DAMAGED_WRITE_OFF",
  "EXPIRED_DESTROY",
];

const bucketOptions = (t: TFn): { value: InventoryImpactType; label: string }[] =>
  BUCKET_CODES.map((value) => ({ value, label: `${value} — ${t(`inventoryImpact.bucket.${value}`)}` }));

interface AllocationRow {
  inventoryImpact: InventoryImpactType | "";
  quantity: number | "";
  bucketLocationCode?: string;
  damageDescription?: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  returnRequestId: string;
  item: IReturnItem | null;
  damagedInventory: IReturnDamagedInventory[];
  onSaved: () => void;
}

export function InventoryImpactModal ({
  opened, onClose, returnRequestId, item, damagedInventory, onSaved
}: Props){
  const { t } = useTranslation("returns");
  const [rows, setRows] = useState<AllocationRow[]>([
    { inventoryImpact: "", quantity: "" }
  ]);
  const [busy, setBusy] = useState(false);

  // Reset form when modal opens for a new item.
  useEffect(() => {
    if (opened) setRows([{ inventoryImpact: "", quantity: "" }]);
  }, [opened, item?.id]);

  const alreadyAllocated = useMemo(() => {
    if (!item) return 0;
    return damagedInventory
      .filter((d) => d.returnItemId === item.id)
      .reduce((sum, d) => sum + d.quantity, 0);
  }, [damagedInventory, item]);

  const remaining = item ? item.quantity - alreadyAllocated : 0;
  const draftSum = rows.reduce(
    (s, r) => s + (typeof r.quantity === "number" ? r.quantity : 0), 0
  );
  const exceeded = draftSum > remaining;
  const valid = rows.every((r) => r.inventoryImpact && typeof r.quantity === "number" && r.quantity > 0)
    && draftSum > 0
    && !exceeded;

  function updateRow (idx: number, patch: Partial<AllocationRow>){
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }
  function addRow (){
    if (rows.length >= BUCKET_CODES.length) return;
    setRows((prev) => [...prev, { inventoryImpact: "", quantity: "" }]);
  }
  function removeRow (idx: number){
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));
  }

  async function handleSave (){
    if (!item || !valid) return;
    try {
      setBusy(true);
      await ReturnApi.setInventoryImpact(returnRequestId, item.id, {
        allocations: rows.map((r) => ({
          inventoryImpact: r.inventoryImpact as InventoryImpactType,
          quantity: r.quantity as number,
          bucketLocationCode: r.bucketLocationCode?.trim() || null,
          damageDescription: r.damageDescription?.trim() || null,
        })),
      });
      showNotification({ color: "green", title: t("toast.success"), message: t("inventoryImpact.allocated") });
      onSaved();
      onClose();
    } catch (e) {
      showNotification({ color: "red", title: t("toast.error"), message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    // Multi-row form: right Drawer 720 below the app header (canvas Ord_Rules «д»).
    <Drawer
      opened={opened}
      onClose={onClose}
      title={t("inventoryImpact.title")}
      position="right"
      size={720}
      styles={{ inner: { top: rem(60), height: `calc(100% - ${rem(60)})` }, title: { fontSize: "var(--nw-fz-drawer)", fontWeight: 650 } }}
    >
      {item ? (
        <Stack gap="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap={2}>
              <Text size="sm" fw={500}>{item.productName}</Text>
              {item.variantName ? <Text size="sm" c="dimmed">{item.variantName}</Text> : null}
              {item.sku ? <Text size="sm" c="dimmed">SKU: {item.sku}</Text> : null}
            </Stack>
            <Stack gap={2} align="flex-end">
              <Text size="sm" c="dimmed">{t("inventoryImpact.totalReturned")}</Text>
              <Badge variant="light">{t("inventoryImpact.pieces", { count: item.quantity })}</Badge>
            </Stack>
          </Group>

          <Group gap="xs">
            <Text size="sm">{t("inventoryImpact.alreadyAllocated")}</Text>
            <Badge color={alreadyAllocated > 0 ? "blue" : "gray"} variant="light">
              {alreadyAllocated} / {item.quantity}
            </Badge>
            <Text size="sm">·</Text>
            <Text size="sm">{t("inventoryImpact.remainingLabel")}</Text>
            <Badge color={remaining > 0 ? "green" : "gray"} variant="light">
              {t("inventoryImpact.pieces", { count: remaining })}
            </Badge>
          </Group>

          {remaining <= 0 ? (
            <Alert
              color="yellow"
              variant="light"
              icon={<WarningCircleIcon size={18} />}
              title={t("inventoryImpact.allAllocatedTitle")}
            >
              {t("inventoryImpact.allAllocatedBody")}
            </Alert>
          ) : (
            <Stack gap="sm">
              {rows.map((row, idx) => {
                const requiresDescription = row.inventoryImpact && row.inventoryImpact !== "RESTOCK_GOOD";
                return (
                  <Stack
                    key={idx}
                    gap="xs"
                    p="sm"
                    style={{
                      border: "1px solid var(--mantine-color-gray-3)",
                      borderRadius: "var(--mantine-radius-md)",
                    }}
                  >
                    <Group justify="space-between">
                      <Title order={6}>{t("inventoryImpact.allocationN", { number: idx + 1 })}</Title>
                      {rows.length > 1 ? (
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={t("inventoryImpact.removeAllocation")}
                          onClick={() => removeRow(idx)}
                        >
                          <TrashIcon size={16} />
                        </ActionIcon>
                      ) : null}
                    </Group>
                    <Group grow align="flex-start">
                      <Select
                        label={t("inventoryImpact.bucketLabel")}
                        placeholder={t("shippingPanel.select")}
                        data={bucketOptions(t)}
                        value={row.inventoryImpact || null}
                        onChange={(v) =>
                          updateRow(idx, { inventoryImpact: (v ?? "") as InventoryImpactType | "" })
                        }
                        required
                        searchable
                      />
                      <NumberInput
                        label={t("inventoryImpact.qty")}
                        placeholder="0"
                        value={row.quantity}
                        onChange={(v) =>
                          updateRow(idx, { quantity: typeof v === "number" ? v : "" })
                        }
                        min={1}
                        max={remaining}
                        required
                      />
                    </Group>
                    <TextInput
                      label={t("inventoryImpact.bucketLocation")}
                      placeholder={t("inventoryImpact.bucketLocationPlaceholder")}
                      value={row.bucketLocationCode ?? ""}
                      onChange={(e) =>
                        updateRow(idx, { bucketLocationCode: e.currentTarget.value })
                      }
                    />
                    {requiresDescription ? (
                      <Textarea
                        label={t("inventoryImpact.damageNote")}
                        placeholder={t("inventoryImpact.damageNotePlaceholder")}
                        value={row.damageDescription ?? ""}
                        onChange={(e) =>
                          updateRow(idx, { damageDescription: e.currentTarget.value })
                        }
                        minRows={2}
                        autosize
                      />
                    ) : null}
                  </Stack>
                );
              })}

              {rows.length < BUCKET_CODES.length ? (
                <Anchor component="button" type="button" onClick={addRow} size="sm">
                  {t("inventoryImpact.addBucket")}
                </Anchor>
              ) : null}

              {exceeded ? (
                <Alert color="gray" variant="light" icon={<WarningCircleIcon size={18} />}>
                  {t("inventoryImpact.exceeded", { draftSum, remaining })}
                </Alert>
              ) : null}
            </Stack>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose} disabled={busy}>
              {t("resolutionPanel.cancel")}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!valid || busy || remaining <= 0}
              loading={busy}
            >
              {t("resolutionPanel.save")}
            </Button>
          </Group>
        </Stack>
      ) : null}
    </Drawer>
  );
}
